import { useReducer, useEffect, useRef, type ReactNode } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { INITIAL_TOKENS } from "../config/constants";
import type { AppState, AppAction } from "./appTypes";
import { computePendingByChatId } from "../utils/chatPending";
import { AppContext } from "./appContext";
import { auth } from "../firebase";
import { observeUserChats } from "../services/chatService";
import { observeUserCharacters, observeUserDocument } from "../services/userService";

// Re-export types for convenience
export type { AuthState, Message, AppState, AppAction } from "./appTypes";

const initialState: AppState = {
  authState: "loggedOut",
  tokens: 0,
  userDisplayName: null,
  activeChatId: null,
  unlockedAssetIds: [],
  unreadByChatId: {},
  pendingByChatId: {},
  autoTopUpEnabled: false,
  chatMessagesByChatId: {},
  hasOpenedChatIds: new Set(),
  chats: [],
  chatsLoading: false,
  chatsError: null,
  characters: [],
  charactersLoading: false,
  charactersError: null,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case "LOGIN":
      return {
        ...state,
        authState: action.payload?.authState || "free",
        tokens: INITIAL_TOKENS,
      };
    case "SIGNUP":
      return {
        ...state,
        authState: "free",
        tokens: INITIAL_TOKENS,
      };
    case "LOGOUT":
      return {
        ...initialState,
      };
    case "SET_AUTH_STATE":
      return {
        ...state,
        authState: action.payload,
      };
    case "SET_TOKENS":
      return {
        ...state,
        tokens: action.payload,
      };
    case "SET_USER_DISPLAY_NAME":
      return {
        ...state,
        userDisplayName: action.payload,
      };
    case "ADD_TOKENS":
      return {
        ...state,
        tokens: state.tokens + action.payload,
      };
    case "DEDUCT_TOKENS":
      return {
        ...state,
        tokens: Math.max(0, state.tokens - action.payload),
      };
    case "SET_ACTIVE_CHAT":
      return {
        ...state,
        activeChatId: action.payload,
      };
    case "SET_PENDING_BY_CHAT":
      return {
        ...state,
        pendingByChatId: action.payload,
      };
    case "SET_CHAT_PENDING": {
      const { chatId, isPending } = action.payload;
      if (!isPending && !state.pendingByChatId[chatId]) {
        return state;
      }
      const next = { ...state.pendingByChatId };
      if (isPending) {
        next[chatId] = true;
      } else {
        delete next[chatId];
      }
      return {
        ...state,
        pendingByChatId: next,
      };
    }
    case "UNLOCK_ASSET":
      if (state.unlockedAssetIds.includes(action.payload)) {
        return state;
      }
      return {
        ...state,
        unlockedAssetIds: [...state.unlockedAssetIds, action.payload],
      };
    case "SET_UNLOCKED_ASSETS":
      return {
        ...state,
        unlockedAssetIds: action.payload,
      };
    case "MARK_CHAT_READ":
      return {
        ...state,
        unreadByChatId: {
          ...state.unreadByChatId,
          [action.payload]: false,
        },
      };
    case "MARK_CHAT_UNREAD":
      return {
        ...state,
        unreadByChatId: {
          ...state.unreadByChatId,
          [action.payload]: true,
        },
      };
    case "TOGGLE_AUTO_TOP_UP":
      return {
        ...state,
        autoTopUpEnabled: !state.autoTopUpEnabled,
      };
    case "ADD_MESSAGE": {
      const { chatId, message } = action.payload;
      const existingMessages = state.chatMessagesByChatId[chatId] || [];
      return {
        ...state,
        chatMessagesByChatId: {
          ...state.chatMessagesByChatId,
          [chatId]: [...existingMessages, message],
        },
      };
    }
    case "MARK_CHAT_OPENED": {
      const newSet = new Set(state.hasOpenedChatIds);
      newSet.add(action.payload);
      return {
        ...state,
        hasOpenedChatIds: newSet,
      };
    }
    case "CANCEL_SUBSCRIPTION":
      return {
        ...state,
        authState: "free",
      };
    case "SET_CHATS":
      return {
        ...state,
        chats: action.payload,
        pendingByChatId: computePendingByChatId(action.payload),
        chatsLoading: false,
        chatsError: null,
      };
    case "SET_CHATS_LOADING":
      return {
        ...state,
        chatsLoading: action.payload,
      };
    case "SET_CHATS_ERROR":
      return {
        ...state,
        chatsError: action.payload,
        chatsLoading: false,
      };
    case "SET_CHARACTERS":
      return {
        ...state,
        characters: action.payload,
        charactersLoading: false,
        charactersError: null,
      };
    case "SET_CHARACTERS_LOADING":
      return {
        ...state,
        charactersLoading: action.payload,
      };
    case "SET_CHARACTERS_ERROR":
      return {
        ...state,
        charactersError: action.payload,
        charactersLoading: false,
      };
    default:
      return state;
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const unsubscribeChatsRef = useRef<(() => void) | null>(null);
  const unsubscribeCharactersRef = useRef<(() => void) | null>(null);
  const unsubscribeUserDocRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      // Clean up previous subscriptions
      if (unsubscribeChatsRef.current) {
        unsubscribeChatsRef.current();
        unsubscribeChatsRef.current = null;
      }
      if (unsubscribeCharactersRef.current) {
        unsubscribeCharactersRef.current();
        unsubscribeCharactersRef.current = null;
      }
      if (unsubscribeUserDocRef.current) {
        unsubscribeUserDocRef.current();
        unsubscribeUserDocRef.current = null;
      }

      if (user) {
        // User is authenticated - update auth state
        dispatch({ type: "LOGIN", payload: { authState: "free" } });

        unsubscribeUserDocRef.current = observeUserDocument(
          user.uid,
          (userDoc) => {
            const unlockedMediaIds = Array.isArray(userDoc?.unlockedMediaIds)
              ? userDoc?.unlockedMediaIds
              : [];
            dispatch({ type: "SET_UNLOCKED_ASSETS", payload: unlockedMediaIds });
            dispatch({
              type: "SET_USER_DISPLAY_NAME",
              payload:
                typeof userDoc?.displayName === "string"
                  ? userDoc.displayName.trim() || null
                  : null,
            });
          },
          (error) => {
            console.error("Failed to observe user document in AppProvider:", error);
            dispatch({ type: "SET_UNLOCKED_ASSETS", payload: [] });
            dispatch({ type: "SET_USER_DISPLAY_NAME", payload: null });
          }
        );
        
        // Start listening for chats
        dispatch({ type: "SET_CHATS_LOADING", payload: true });
        unsubscribeChatsRef.current = observeUserChats(
          user.uid,
          (chats) => {
            dispatch({ type: "SET_CHATS", payload: chats });
          },
          (error) => {
            dispatch({ type: "SET_CHATS_ERROR", payload: error.message });
          }
        );

        // Start listening for characters
        dispatch({ type: "SET_CHARACTERS_LOADING", payload: true });
        unsubscribeCharactersRef.current = observeUserCharacters(
          user.uid,
          (characters) => {
            dispatch({ type: "SET_CHARACTERS", payload: characters });
          },
          (error) => {
            dispatch({ type: "SET_CHARACTERS_ERROR", payload: error.message });
          }
        );
      } else {
        // User logged out - reset state
        dispatch({ type: "LOGOUT" });
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeChatsRef.current?.();
      unsubscribeCharactersRef.current?.();
      unsubscribeUserDocRef.current?.();
    };
  }, []);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

